import React, { useState, useEffect, useRef, cloneElement } from 'react';
import { SearchState } from '@customTypes/search';
import Button from '@solar/common/Button/ButtonV2';
import { CloseIcon } from '@resortpass/solar-icons';
import SearchProvider, {
  useSearchContext,
  useDefaultContextImplementation,
} from '@context/SearchContext2025';
import { useDrawer } from '@context/DrawerContext';

/**
 * MobileSRPDrawerInterface is an interface that can be fulfilled by the
 * contents of the mobile drawer.
 *
 * It allows invocation of a customized function for the "clear all"
 * ald "apply" buttons in the mobile drawer.
 */
export type MobileSRPDrawerInterface = {
  onApply?: (s: SearchState) => void;
  onClear?: () => void;
  onClose?: () => void;
};

type Props = {
  title?: string;
  children?: React.ReactNode;
};

/**
 * MobileSRPDrawer is a component that renders a controls to be used in a drawer
 * for search filters.
 */
export default function MobileSRPDrawer({ title = 'Filters', children }: Props) {
  const { closeDrawer } = useDrawer();
  const searchContext = useSearchContext();
  const { searchParams } = searchContext;
  const childSearchContext = useDefaultContextImplementation(searchParams);
  const callbackRef = useRef<MobileSRPDrawerInterface>(null);

  const [fireSearch, setFireSearch] = useState(false);
  useEffect(() => {
    if (fireSearch) {
      searchContext.search(1);
      setFireSearch(false);
      closeDrawer();
    }
  }, [fireSearch, searchContext, closeDrawer]);

  const applyFilters = (s: SearchState) => {
    if (callbackRef.current && callbackRef.current.onApply) {
      callbackRef.current.onApply(s);
    }

    searchContext.updateSearchState(s);
    setFireSearch(true);
  };

  const clearFilters = () => {
    if (callbackRef.current && callbackRef.current.onClear) {
      callbackRef.current.onClear();
    }
    childSearchContext.clearFilters();
  };

  const closeFilters = () => {
    if (callbackRef.current && callbackRef.current.onClose) {
      callbackRef.current.onClose();
    }
    closeDrawer();
  };

  const childWithRef = cloneElement(children as React.ReactElement<any>, { ref: callbackRef });

  return (
    <div className="w-full relative overflow-y-auto bottom-0 flex flex-col">
      <div className="flex-grow-0 flex-shrink-0 desktop:hidden sticky top-0 px-4 py-3 text-15 font-rp-pn-semi-bold w-full rounded-t-xl bg-white border-b border-rp-gray-divider z-100">
        <div className="font-rp-basetica text-center text-base text-solar-primary relative">
          <button
            type="button"
            className="absolute absolute-vertical-center right-0"
            onClick={closeFilters}
            data-testid="close-button"
          >
            <CloseIcon width={24} height={24} fill="#22272A" />
          </button>
          <span>{title}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <SearchProvider value={childSearchContext}>{childWithRef}</SearchProvider>
      </div>
      <div className="flex-grow-0 flex-shrink-0 bg-white w-full p-spacing-md border-t border-rp-gray-divider">
        {/* Book now button */}
        <div className="h-12 font-rp-basetica flex justify-between items-center">
          <button
            type="button"
            className="text-base text-solar-tertiary cursor-pointer"
            onClick={clearFilters}
          >
            Clear all
          </button>

          <Button
            fullWidth={false}
            size="lg"
            onClick={() => applyFilters(childSearchContext.searchParams)}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
